import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { SearchCommand } from "@/components/search-command"
import { cn } from "@/lib/utils"
import { Menu } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import aboutData from "@/content/about.json"

export function Navigation() {
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
    const isActive = location.pathname === to ||
      (to !== '/' && location.pathname.startsWith(to));
    return (
      <Link
        to={to}
        aria-current={isActive ? 'page' : undefined}
        onClick={() => setSheetOpen(false)}
        className={cn(
          "text-sm transition-colors py-1 focus:outline-none rounded-sm relative",
          isActive
            ? "text-foreground"
            : "text-muted-foreground hover:text-foreground"
        )}
      >
        {children}
        {isActive && (
          <span className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-warm-accent" />
        )}
      </Link>
    );
  };

  const ExternalLink = ({ href, children }: { href: string; children: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
    >
      {children}
    </a>
  );

  const MobileNav = () => (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="min-[1024px]:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[280px] bg-background border-border">
        <nav className="flex flex-col gap-3 mt-8">
          <NavLink to="/">Home</NavLink>
          <NavLink to="/projects">Projects</NavLink>
          <NavLink to="/blog">Blog</NavLink>
        </nav>

        <div className="mt-6 border-t border-border/50 pt-6">
          <div className="flex flex-col gap-3">
            <ExternalLink href={aboutData.resumeUrl}>Resume</ExternalLink>
            <ExternalLink href={aboutData.meetingUrl}>Meeting</ExternalLink>
            <ExternalLink href="mailto:awyawhare@gmail.com">Email</ExternalLink>
            <ExternalLink href="https://linkedin.com/in/arinjay-wyawhare">LinkedIn</ExternalLink>
            <ExternalLink href="https://github.com/jaywyawhare">GitHub</ExternalLink>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <header className="fixed top-0 w-full z-50 bg-background/95 backdrop-blur-sm border-b border-border/40">
      <div className="mx-auto h-14 flex items-center justify-between max-w-2xl px-6">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center">
            <span className="text-sm font-mono text-muted-foreground hover:text-foreground transition-colors duration-200">
              {aboutData.name.split(' ').map(n => n[0]).join('')}
            </span>
          </Link>

          <div className="hidden min-[1024px]:flex items-center gap-4 text-sm">
            <ExternalLink href={aboutData.resumeUrl}>Resume</ExternalLink>
            <ExternalLink href={aboutData.meetingUrl}>Meeting</ExternalLink>
            <ExternalLink href="mailto:awyawhare@gmail.com">Email</ExternalLink>
            <ExternalLink href="https://linkedin.com/in/arinjay-wyawhare">LinkedIn</ExternalLink>
            <ExternalLink href="https://github.com/jaywyawhare">GitHub</ExternalLink>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <nav className="hidden min-[1024px]:flex items-center gap-5">
            <NavLink to="/">Home</NavLink>
            <NavLink to="/projects">Projects</NavLink>
            <NavLink to="/blog">Blog</NavLink>
          </nav>
          <SearchCommand />
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
